import { Button } from "@/components/ui/button";
import { RHF } from "@/shared/forms";
import { cn } from "@/shared/lib/utils";
import { LocateFixed } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { BranchMap } from "./BranchMap";

type BranchMapPickerProps = {
  /** react-hook-form field prefix — defaults to root lat/lng/serviceRadiusM */
  latName?: string;
  lngName?: string;
  radiusName?: string;
  className?: string;
};

export function BranchMapPicker({
  latName = "lat",
  lngName = "lng",
  radiusName = "serviceRadiusM",
  className,
}: BranchMapPickerProps) {
  const { watch, setValue } = RHF.useFormContext<{
    lat?: number;
    lng?: number;
    serviceRadiusM?: number;
  }>();
  const [locating, setLocating] = useState(false);

  const lat = watch(latName as "lat");
  const lng = watch(lngName as "lng");
  const serviceRadiusM = watch(radiusName as "serviceRadiusM");

  const hasPoint =
    lat != null &&
    lng != null &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng);

  const handleChange = (nextLng: number, nextLat: number) => {
    setValue(lngName as "lng", nextLng, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(latName as "lat", nextLat, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleUseCurrentLocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("ເບຣາວເຊີບໍ່ຮອງຮັບຕຳແຫນ່ງ");
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        handleChange(pos.coords.longitude, pos.coords.latitude);
        setLocating(false);
        toast.success("ປັກມຸດຕຳແຫນ່ງປັດຈຸບັນແລ້ວ");
      },
      (error) => {
        setLocating(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error("ບໍ່ໄດ້ຮັບອະນຸຍາດໃຊ້ຕຳແຫນ່ງ");
          return;
        }
        toast.error("ຫາຕຳແຫນ່ງປັດຈຸບັນບໍ່ໄດ້");
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 },
    );
  };

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="mb-2 flex shrink-0 justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-[12px]"
          disabled={locating}
          onClick={handleUseCurrentLocation}
        >
          <LocateFixed className="h-4 w-4" />
          {locating ? "ກຳລັງຫາຕຳແຫນ່ງ..." : "ປັກມຸດຕຳແຫນ່ງປັດຈຸບັນ"}
        </Button>
      </div>

      <div className="min-h-0 flex-1">
        <BranchMap
          className="h-full min-h-[240px]"
          branches={[]}
          showRadius={hasPoint && (serviceRadiusM ?? 0) > 0}
          pickerMode={
            hasPoint
              ? {
                  lng: lng!,
                  lat: lat!,
                  radiusM: serviceRadiusM ?? undefined,
                  onChange: handleChange,
                }
              : {
                  lng: 102.6331,
                  lat: 17.9757,
                  radiusM: serviceRadiusM ?? undefined,
                  onChange: handleChange,
                }
          }
        />
      </div>

      <p className="mt-2 shrink-0 text-muted-foreground text-xs">
        ຄລິກຫຼືລາກ marker ເພື່ອກຳນົດຕຳແໜ່ງສາຂາ — ຫຼືໃຊ້ປຸ່ມປັກມຸດຕຳແຫນ່ງປັດຈຸບັນ
      </p>
    </div>
  );
}
